import { Entity, Index, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { Column } from '../../decorators';
import { OrderStatus } from '../../enums';
import { formatEnumAsSelectOptions } from '../../utils';
import { BaseEntity } from './base.entity';
import { ClientAddressItemEntity } from './client-address-item.entity';
import { ClientAddressEntity } from './client-address.entity';
import { ClientItemEntity } from './client-item.entity';
import { ClientEntity } from './client.entity';
import { CompanyEntity } from './company.entity';
import { MPaymentPaymentMethodEntity } from './m-payment-payment-method.entity';
import { MSmClientOpportunityEntity } from './m-sm-client-opportunity.entity';
import { MSmPaymentTransactionOrderEntity } from './m-sm-payment-transaction-order.entity';
import { OrderDetailEntity } from './order-detail.entity';
import { OrderFileEntity } from './order-file.entity';
import { OrderNoteEntity } from './order-note.entity';
import { PublicUserEntity } from './public-user.entity';
import { ShippingMethodEntity } from './shipping-method.entity';
import { TaxEntity } from './tax.entity';
import { TermsCodeEntity } from './terms-code.entity';
import { TripStopEntity } from './trip-stop.entity';
import { UserEntity } from './user.entity';

@Index('externalId_fk_company__id', ['externalId', 'fkCompanyId'], {
	unique: true,
})
@Index('fk_company__id', ['fkCompanyId'])
@Index('fk_public_user__id_override', ['fkPublicUserIdOverride'])
@Index('fk_public_user__id_submitter', ['fkPublicUserIdSubmitter'])
@Index('fk_tax__id', ['fkTaxId'])
@Index('fk_user__id_override', ['fkUserIdOverride'])
@Index('fk_user__id_submitter', ['fkUserIdSubmitter'])
@Index('fx_order_fulltext', ['externalId', 'customerPO'], { fulltext: true })
// PD-14788: covering index for the invoice keyword search's customer-PO
// id-resolution query (DERIVED_KEYWORD_ID_QUERIES in typeormFilterBuilder.ts,
// which names it in a USE INDEX hint). The LIKE has a leading wildcard so no
// seek on customerPO is possible — the win is ref-access on the
// _deleted_date IS NULL prefix with customerPO evaluated inside this narrow
// covering index (both columns + implicit PK) instead of a row lookup per
// live order: ~0.1s vs ~0.8s on pharmsource_staging. The hint is required —
// the 5.7 optimizer prefers the non-covering _deleted_date ref on
// idx_order_deleted_company_date on its own, whichever column order this
// index uses.
@Index('idx_order_deleted_customer_po', ['deletedDate', 'customerPO'])
@Index('m_sm_payment_fk_m_payment_method__id', ['mSmPaymentFkMPaymentMethodId'])
@Index('order__fk_client__id_fk', ['fkClientId'])
@Index('idx_order_billing_account', ['fkClientIdBilling'], {})
@Index('order__fk_client__id_delivery_fk', ['fkClientIdDelivery'])
@Index('order__fk_client_address__id_billing_fk', ['fkClientAddressIdBilling'])
@Index('order__fk_client_address__id_shipping_fk', [
	'fkClientAddressIdShipping',
])
@Index('order__fk_user__id_fk', ['fkUserId'])
@Index('order_externalId_fk_company__id__deleted_userId', [
	'externalId',
	'fkCompanyId',
	'deletedUserId',
])
@Index('idx_order_company_status_deleted_date', [
	'fkCompanyId',
	'deletedDate',
	'status',
	'date',
])
@Index('idx_order_deleted_company_date', ['deletedDate', 'fkCompanyId', 'date'])
@Index('idx_order_client_status_deleted', [
	'fkClientId',
	'status',
	'deletedDate',
])
@Entity('order')
export class OrderEntity extends BaseEntity {
	@Column('varchar', { name: 'externalId', nullable: true, length: 64 })
	public externalId!: string | null;

	@Column('datetime', { name: 'date', nullable: true })
	public date!: Date | null;

	@Column('int', { name: '_fk_client__id', unsigned: true })
	public fkClientId!: number;

	/**
	 * PD-15849 — the billing account this order belongs to: the ordering
	 * client, or its parent when the client is billed with parent. Stamped
	 * at creation from the client's resolved billing account and never
	 * re-derived afterwards, because flipping "Bill with parent" is
	 * non-retroactive — only an explicit move restates existing rows.
	 *
	 * Declared NULLABLE deliberately. Our database sync is additive only
	 * (it never ALTERs an existing column), so a NOT NULL column could not
	 * be added to a table that already has rows. The PD-15849 migration
	 * backfills `fk_client__id_billing = _fk_client__id` for every existing
	 * row, but the column stays nullable so a tenant whose migration has
	 * not run yet still reads correctly: NULL means "billed to
	 * `_fk_client__id`". Read it through `billingAccountOf`
	 * (`libs/shared/src/utils/billing-account.ts`), never directly.
	 */
	@Column('int', {
		name: 'fk_client__id_billing',
		nullable: true,
		unsigned: true,
		comment:
			'Billing account for this order (the parent when billed with parent). NULL = billed to _fk_client__id.',
	})
	public fkClientIdBilling!: number | null;

	@Column('int', { name: '_fk_user__id', nullable: true, unsigned: true })
	public fkUserId!: number | null;

	@Column('int', { name: 'numberOfLines', nullable: true })
	public numberOfLines!: number | null;

	@Column('decimal', {
		name: 'subtotal',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public subtotal!: string | null;

	@Column('decimal', {
		name: 'freight',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public freight!: string | null;

	// PD-14375 — the pre-promo freight base the recalc pipeline computed
	// (shipping-method effective price incl. free-shipping threshold +
	// surcharges, or a rep's manual entry). `freight` holds the FINAL
	// charged value (base minus any shipping-promo discount); this column
	// anchors the promo math so re-runs discount the base rather than
	// compounding on an already-discounted `freight`, and a lapsed
	// shipping promo restores `freight` back to the base. NULL on orders
	// that predate the column or never went through the recalc pipeline —
	// consumers fall back to `freight`.
	@Column('decimal', {
		name: 'freightBase',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public freightBase!: string | null;

	@Column('decimal', { name: 'tax', nullable: true, precision: 13, scale: 2 })
	public tax!: string | null;

	@Column('decimal', {
		name: 'discount',
		nullable: true,
		precision: 21,
		scale: 10,
	})
	public discount!: string | null;

	@Column('decimal', { name: 'total', nullable: true, precision: 13, scale: 2 })
	public total!: string | null;

	@Column('decimal', {
		name: 'totalGP',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public totalGp!: string | null;

	@Column(
		'varchar',
		{
			name: 'status',
			nullable: true,
			length: 255,
			default: () => `'${OrderStatus.UNSUBMITTED}'`,
		},
		{
			selectOptions: formatEnumAsSelectOptions(OrderStatus),
		},
	)
	public status!: OrderStatus | null;

	@Column('varchar', { name: 'customerPO', nullable: true, length: 255 })
	public customerPO!: string | null;

	@Column('datetime', { name: 'lastImportDate', nullable: true })
	public lastImportDate!: Date | null;

	@Column('varchar', { name: 'alertStatus', nullable: true, length: 255 })
	public alertStatus!: string | null;

	@Column('tinyint', { name: 'canCancel', width: 1, default: () => "'0'" })
	public canCancel!: boolean;

	@Column('varchar', { name: 'origin', length: 255, default: () => "'2'" })
	public origin!: string;

	@Column('varchar', { name: 'shipVia', nullable: true, length: 255 })
	public shipVia!: string | null;

	@Column('text', { name: 'statusText', nullable: true })
	public statusText!: string | null;

	@Column('varchar', { name: 'externalId2', nullable: true, length: 255 })
	public externalId2!: string | null;

	@Column('date', { name: 'shippedByDate', nullable: true })
	public shippedByDate!: string | null;

	@Column('int', {
		name: 'fk_user__id_submitter',
		nullable: true,
		unsigned: true,
	})
	public fkUserIdSubmitter!: number | null;

	@Column('int', {
		name: '_fk_client_address__id_billing',
		nullable: true,
		unsigned: true,
	})
	public fkClientAddressIdBilling!: number | null;

	@Column('int', {
		name: '_fk_client_address__id_shipping',
		nullable: true,
		unsigned: true,
	})
	public fkClientAddressIdShipping!: number | null;

	// PD-15423 — cross-account delivery: when set, the order is delivered to
	// THIS client's site (fkClientAddressIdShipping then points at one of the
	// delivery client's addresses) while the order and invoice stay on
	// fkClientId. Null = normal delivery to the ordering client.
	@Column('int', {
		name: 'fk_client__id_delivery',
		nullable: true,
		unsigned: true,
	})
	public fkClientIdDelivery!: number | null;

	@Column('date', { name: 'expirationDate', nullable: true })
	public expirationDate!: string | null;

	@Column('int', {
		name: 'fk_public_user__id_submitter',
		nullable: true,
		unsigned: true,
	})
	public fkPublicUserIdSubmitter!: number | null;

	@Column('int', {
		name: 'fk_company__id',
		unsigned: true,
		default: () => "'1'",
	})
	public fkCompanyId!: number;

	@Column('longtext', { name: 'approvalRequiredReason', nullable: true })
	public approvalRequiredReason!: string | null;

	@Column('datetime', { name: 'overrideDateTime', nullable: true })
	public overrideDateTime!: Date | null;

	@Column('int', {
		name: 'fk_user__id_override',
		nullable: true,
		unsigned: true,
	})
	public fkUserIdOverride!: number | null;

	@Column('decimal', {
		name: 'totalGPMargin',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public totalGpMargin!: string | null;

	@Column('int', {
		name: 'm_sm_payment_fk_m_payment_method__id',
		nullable: true,
		unsigned: true,
	})
	public mSmPaymentFkMPaymentMethodId!: number | null;

	/**
	 * PD-15847 — the payment method TYPE this order is to be settled with.
	 *
	 * The FK above only names a saved (vaulted) row, so a Company Credit,
	 * Cash or Check order was previously indistinguishable from one whose
	 * method was simply never chosen. Stamped from the client's resolved
	 * default at creation and overwritten by an explicit choice at submit.
	 *
	 * Invariant: when `mSmPaymentFkMPaymentMethodId` is set, this equals
	 * that row's `mPaymentType`. NULL on pre-existing orders — readers
	 * derive from the FK row when present, else treat it as unknown.
	 */
	@Column('varchar', {
		name: 'paymentMethodType',
		nullable: true,
		length: 64,
		comment:
			'Payment method type for this order (PaymentMethodTypeEnum value). Matches the saved method row when one is linked.',
	})
	public paymentMethodType!: string | null;

	/**
	 * PD-15848 — a per-ORDER terms override (QuickBooks-style).
	 *
	 * The client's `fkTermsCodeId` remains the default; this only names a
	 * different terms code for this one order, and
	 * `InvoicesService._resolveEffectiveTermsCode` prefers it over the
	 * client's when the invoice is raised. NULL — every existing order —
	 * means "use the client's terms", which is exactly what happened
	 * before this column existed.
	 */
	@Column('int', {
		name: 'fk_terms_code__id',
		nullable: true,
		unsigned: true,
		comment:
			'Per-order payment terms override. NULL = use the client terms code.',
	})
	public fkTermsCodeId!: number | null;

	@Column('varchar', {
		name: 'm_sm_payment_fee_unit',
		nullable: true,
		length: 32,
	})
	public mSmPaymentFeeUnit!: string | null;

	@Column('decimal', {
		name: 'm_sm_payment_fee_amount',
		precision: 13,
		scale: 2,
		default: () => "'0.00'",
	})
	public mSmPaymentFeeAmount!: string;

	/**
	 * PD-15143 — the processing fee on this order, in DOLLARS, stored rather
	 * than derived. `total` includes it.
	 *
	 * The order needs its own figure and cannot lean on the invoice: on the
	 * charge-at-submit flows the money moves before any invoice exists. Same
	 * basis rule as the invoice — projected from the stamped rate until
	 * payments post, then the sum of those transactions' actual fees.
	 */
	@Column('decimal', {
		name: 'm_sm_payment_fee_total',
		precision: 13,
		scale: 2,
		default: () => "'0.00'",
	})
	public mSmPaymentFeeTotal!: string;

	@Column('decimal', {
		name: 'shipCost',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public shipCost!: string | null;

	@Column('varchar', { name: 'discountType', length: 32, default: () => "'$'" })
	public discountType!: string;

	@Column('int', { name: 'fk_tax__id', nullable: true, unsigned: true })
	public fkTaxId!: number | null;

	@Column('decimal', {
		name: 'taxRate',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public taxRate!: string | null;

	@Column('int', {
		name: 'fk_public_user__id_override',
		nullable: true,
		unsigned: true,
	})
	public fkPublicUserIdOverride!: number | null;

	@Column('varchar', { name: 'jobNumber', nullable: true, length: 64 })
	public jobNumber!: string | null;

	@Column('varchar', { name: 'internalReference', nullable: true, length: 64 })
	public internalReference!: string | null;

	@Column('int', {
		name: 'fk_shipping_method__id',
		nullable: true,
		unsigned: true,
	})
	public fkShippingMethodId!: number | null;

	@Column('json', {
		name: 'appliedCouponCodes',
		nullable: true,
		comment:
			'Array of coupon codes entered against this cart/order. Persisted on the cart so codes survive page reloads and the promo engine sees them on every recompute. Null/empty array = no codes entered.',
	})
	public appliedCouponCodes!: Array<string> | null;

	@Column('json', {
		name: 'customFields',
		nullable: true,
		comment: 'JSON key-value pairs for tenant-specific custom fields.',
	})
	public customFields!: Record<string, unknown> | null;

	@OneToMany(() => ClientEntity, (client) => client.fkOrderIdLast2, {})
	public clients!: Array<ClientEntity>;

	@OneToMany(() => ClientEntity, (client) => client.fkOrder, {})
	public clients2!: Array<ClientEntity>;

	@OneToMany(
		() => ClientAddressItemEntity,
		(clientAddressItem) => clientAddressItem.fkOrderIdLast2,
		{},
	)
	public clientAddressItems!: Array<ClientAddressItemEntity>;

	@OneToMany(
		() => ClientItemEntity,
		(clientItem) => clientItem.fkOrderIdLast2,
		{},
	)
	public clientItems!: Array<ClientItemEntity>;
	@OneToMany(() => ClientItemEntity, (clientItem) => clientItem.fkOrder, {})
	public clientItems2!: Array<ClientItemEntity>;

	@OneToMany(
		() => MSmClientOpportunityEntity,
		(mSmClientOpportunity) => mSmClientOpportunity.mSmFkOrder,
		{},
	)
	public mSmClientOpportunities!: Array<MSmClientOpportunityEntity>;

	@OneToMany(
		() => MSmPaymentTransactionOrderEntity,
		(mSmPaymentTransactionOrder) =>
			mSmPaymentTransactionOrder.mSmPaymentFkOrder,
		{},
	)
	public mSmPaymentTransactionOrders!: Array<MSmPaymentTransactionOrderEntity>;

	@ManyToOne(() => UserEntity, (user) => user.submittedOrders, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_user__id_submitter', referencedColumnName: 'id' }])
	public submittedByUser!: UserEntity;

	@ManyToOne(() => PublicUserEntity, (publicUser) => publicUser.orders, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([
		{ name: 'fk_public_user__id_submitter', referencedColumnName: 'id' },
	])
	public submittedByPublicUser!: PublicUserEntity;

	@ManyToOne(() => CompanyEntity, (company) => company.orders, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_company__id', referencedColumnName: 'id' }])
	public fkCompany!: CompanyEntity;

	@ManyToOne(() => UserEntity, (user) => user.overriddenOrders, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_user__id_override', referencedColumnName: 'id' }])
	public fkUserIdOverride2!: UserEntity;

	@ManyToOne(
		() => MPaymentPaymentMethodEntity,
		(mPaymentPaymentMethod) => mPaymentPaymentMethod.orders,
		{ onDelete: 'NO ACTION', onUpdate: 'NO ACTION' },
	)
	@JoinColumn([
		{
			name: 'm_sm_payment_fk_m_payment_method__id',
			referencedColumnName: 'id',
		},
	])
	public mSmPaymentFkMPaymentMethod!: MPaymentPaymentMethodEntity;

	@ManyToOne(() => TaxEntity, (tax) => tax.orders, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_tax__id', referencedColumnName: 'id' }])
	public fkTax!: TaxEntity;

	@ManyToOne(() => PublicUserEntity, (publicUser) => publicUser.orders2, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([
		{ name: 'fk_public_user__id_override', referencedColumnName: 'id' },
	])
	public fkPublicUserIdOverride2!: PublicUserEntity;

	@ManyToOne(
		() => ClientAddressEntity,
		(clientAddress) => clientAddress.orders,
		{
			onDelete: 'RESTRICT',
			onUpdate: 'RESTRICT',
		},
	)
	@JoinColumn([
		{ name: '_fk_client_address__id_billing', referencedColumnName: 'id' },
	])
	public fkClientAddressIdBilling2!: ClientAddressEntity;

	@ManyToOne(
		() => ClientAddressEntity,
		(clientAddress) => clientAddress.orders2,
		{
			onDelete: 'RESTRICT',
			onUpdate: 'RESTRICT',
		},
	)
	@JoinColumn([
		{ name: '_fk_client_address__id_shipping', referencedColumnName: 'id' },
	])
	public fkClientAddressIdShipping2!: ClientAddressEntity;

	@ManyToOne(() => ClientEntity, (client) => client.orders, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity;

	// PD-15849 — the billing account (see `fkClientIdBilling`).
	@ManyToOne(() => ClientEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_client__id_billing', referencedColumnName: 'id' }])
	public fkClientBilling!: ClientEntity | null;

	// PD-15423 — the delivery-destination client for cross-account delivery.
	// No inverse relation on ClientEntity: read via the received-deliveries
	// query path, not from the client side.
	@ManyToOne(() => ClientEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_client__id_delivery', referencedColumnName: 'id' }])
	public fkDeliveryClient!: ClientEntity | null;

	@ManyToOne(() => UserEntity, (user) => user.assignedOrders, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_user__id', referencedColumnName: 'id' }])
	public fkUser!: UserEntity;

	@ManyToOne(
		() => ShippingMethodEntity,
		(shippingMethod) => shippingMethod.orders,
		{
			onDelete: 'SET NULL',
			onUpdate: 'CASCADE',
		},
	)
	@JoinColumn([{ name: 'fk_shipping_method__id', referencedColumnName: 'id' }])
	public shippingMethod!: ShippingMethodEntity;

	@ManyToOne(() => TermsCodeEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'CASCADE',
	})
	@JoinColumn([{ name: 'fk_terms_code__id', referencedColumnName: 'id' }])
	public fkTermsCode!: TermsCodeEntity | null;

	@OneToMany(() => OrderFileEntity, (orderFile) => orderFile.fkOrder, {})
	public orderFiles!: Array<OrderFileEntity>;

	@OneToMany(() => OrderDetailEntity, (orderDetail) => orderDetail.order, {})
	public orderDetails!: Array<OrderDetailEntity>;

	@OneToMany(() => OrderNoteEntity, (orderNote) => orderNote.fkOrder, {})
	public orderNotes!: Array<OrderNoteEntity>;

	// Distribution refactor: order ↔ trip_stop linkage.
	@Column('int', {
		name: 'fk_trip_stop__id',
		unsigned: true,
		nullable: true,
	})
	public fkTripStopId!: number | null;

	@ManyToOne(() => TripStopEntity, { onDelete: 'SET NULL' })
	@JoinColumn([{ name: 'fk_trip_stop__id', referencedColumnName: 'id' }])
	public fkTripStop?: TripStopEntity;
}
